// src/pages/Documents.tsx

import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react'
import * as XLSX from 'xlsx'
import {
  DataGrid,
  GridRowModes,
  GridActionsCellItem,
  GridColDef
} from '@mui/x-data-grid'
import {
  TextField,
  Button,
  Checkbox,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
  MenuItem,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  LinearProgress,
  Tooltip
} from '@mui/material'
import Autocomplete, { createFilterOptions } from '@mui/material/Autocomplete'
import Popper from '@mui/material/Popper'
import { apiUrl } from '../utils/api'
import EditIcon from '@mui/icons-material/Edit'
import DeleteIcon from '@mui/icons-material/Delete'
import SaveIcon from '@mui/icons-material/Save'
import CancelIcon from '@mui/icons-material/Close'
import FilterAltOffIcon from '@mui/icons-material/FilterAltOff'
import WarningIcon from '@mui/icons-material/Warning'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'

const Documents = ({ projectId, access = 'none', deleteAllAccess = 'none', userId }) => {
  const readOnly = access !== 'write'
  const canDeleteAll = deleteAllAccess === 'write'
  // list of documents
  const [documents, setDocuments] = useState([])
  // track which rows are in edit / view mode
  const [rowModesModel, setRowModesModel] = useState({})
  // options for design change dropdown
  const [designOptions, setDesignOptions] = useState([])
  const [locationList, setLocationList] = useState([])
  const [categoryList, setCategoryList] = useState([])
  const [statusList, setStatusList] = useState([])
  const [copiedCell, setCopiedCell] = useState(null)
  const designFilter = createFilterOptions({
    matchFrom: 'any',
    stringify: (option: any) => `${option.change_number} ${option.change_name}`
  })

  // filter state for each column
  const [numFilter, setNumFilter] = useState([])
  const [nameFilter, setNameFilter] = useState([])
  const [locFilter, setLocFilter] = useState([])
  const [catFilter, setCatFilter] = useState([])
  const [revFilter, setRevFilter] = useState([])
  const [statusFilter, setStatusFilter] = useState([])
  const [issueDateFilter, setIssueDateFilter] = useState([])
  const [reasonFilter, setReasonFilter] = useState([])
  const [changeFilter, setChangeFilter] = useState([])
  const [warningsOnly, setWarningsOnly] = useState(false)
  const [expandedDocs, setExpandedDocs] = useState([])
  const [addOpen, setAddOpen] = useState(false)
  const [filtersOpen, setFiltersOpen] = useState(false)

  const [importing, setImporting] = useState(false)
  const [importProgress, setImportProgress] = useState(0)
  const [reportInfo, setReportInfo] = useState(null)
  const [importPreviewOpen, setImportPreviewOpen] = useState(false)
  const [importPreviewData, setImportPreviewData] = useState<any>(null)
  const [page, setPage] = useState(0)
  const [pageSize, setPageSize] = useState(10)
  const [loading, setLoading] = useState(true)
  const [deletingAll, setDeletingAll] = useState(false)
  const [exporting, setExporting] = useState(false)
  const [exportProgress, setExportProgress] = useState(0)
  const [exportFile, setExportFile] = useState(null)

  const warningColorClass = 'warning-cell'
  const getSubRowClass = row => (row.isSubRow ? 'sub-revision-row' : '')

  // refs and handlers for template import/export
  const fileInputRef = useRef(null)
  const [importSourceOpen, setImportSourceOpen] = useState(false)
  const [mappingOpen, setMappingOpen] = useState(false)
  const [mapFile, setMapFile] = useState(null)
  const [fileHeaders, setFileHeaders] = useState([])
  const mappingFields = [
    'document_number',
    'document_name',
    'document_location',
    'document_category',
    'document_revision',
    'document_status',
    'issue_date',
    'resubmission_reason'
  ]
  const [mapping, setMapping] = useState(
    mappingFields.reduce((acc, f) => ({ ...acc, [f]: '' }), {})
  )
  const [dateFormat, setDateFormat] = useState('')

  const dropdownPopperModifiers = useMemo(
    () => [
      {
        name: 'computeStyles',
        options: {
          adaptive: false
        }
      }
    ],
    []
  )

  useEffect(() => {
    if (!importing) return
    const timer = setInterval(() => {
      setImportProgress(p => (p < 90 ? p + 5 : p))
    }, 500)
    return () => clearInterval(timer)
  }, [importing])

  useEffect(() => {
    if (!exporting) return
    const timer = setInterval(() => {
      setExportProgress(p => (p < 90 ? p + 5 : p))
    }, 500)
    return () => clearInterval(timer)
  }, [exporting])

  const CustomPopper = React.forwardRef(function CustomPopper(props: any, ref: any) {
    const { style, modifiers, ...popperProps } = props
    return (
      <Popper
        {...popperProps}
        ref={ref}
        modifiers={[...(modifiers || []), ...dropdownPopperModifiers]}
        style={{
          ...(style || {}),
          width: 'max-content',
          minWidth: '200px',
          maxWidth: '600px'
        }}
      />
    )
  })

  useEffect(() => {
    if (!mapFile) { setFileHeaders([]); return }
    const reader = new FileReader()
    reader.onload = e => {
      try {
        if (e.target?.result instanceof ArrayBuffer) {
          const data = new Uint8Array(e.target.result)
          const wb = XLSX.read(data, { type: 'array' })
          const ws = wb.Sheets[wb.SheetNames[0]]
          const firstRow = (XLSX.utils.sheet_to_json(ws, { header: 1 })[0] || []) as any[]
          const headers = firstRow.map(h => (h != null ? String(h) : ''))
          setFileHeaders(headers)
        }
      } catch (err) {
        console.error('Failed to read headers', err)
        setFileHeaders([])
      }
    }
    reader.readAsArrayBuffer(mapFile)
  }, [mapFile])

  const handleDownloadTemplate = () => {
    fetch(apiUrl(`/api/documents/template?project_id=${projectId}`))
      .then(res => res.blob())
      .then(blob => {
        const url = window.URL.createObjectURL(blob)
        const a = document.createElement('a')
        a.href = url
        a.download = 'documents_template.xlsx'
        a.click()
        window.URL.revokeObjectURL(url)
      })
      .catch(() => alert('Failed to download template'))
  }

  const downloadImportReport = (file, failedRows: any[] = [], skippedRows: any[] = [], overwrite = false, preview: any = {}, rowStatuses: Record<number, string> = {}) => {
    const reader = new FileReader()
    reader.onload = e => {
      try {
        const data = new Uint8Array(e.target?.result as ArrayBuffer)
        const wb = XLSX.read(data, { type: 'array' })
        const ws = wb.Sheets[wb.SheetNames[0]]
        const rows = XLSX.utils.sheet_to_json(ws, { header: 1 }) as any[][]
        const failedMap: Record<number, string[]> = {}
        failedRows.forEach((f: any) => { failedMap[f.row] = f.errors || [] })
        const globalError = preview?.error || null
        const logRows: { row_no: number; document_number: string; status: string; notes: string }[] = []
        rows.slice(1).forEach((row, i) => {
          const rowIdx = i + 2
          const docNum = row[0] != null ? String(row[0]).trim() : ''
          if (!docNum) return
          if (globalError) {
            logRows.push({ row_no: rowIdx, document_number: docNum, status: 'Failed', notes: globalError })
            return
          }
          const errors = failedMap[rowIdx]
          const backendStatus = rowStatuses[rowIdx]
          let status: string
          let notes: string
          if (errors) {
            status = 'Failed'
            notes = errors.join('; ')
          } else if (backendStatus === 'Skipped') {
            status = 'Pass'
            notes = 'Existing kept (skipped)'
          } else if (backendStatus === 'Overwritten') {
            status = 'Pass'
            notes = 'Item overwritten'
          } else if (backendStatus === 'Added') {
            status = 'Pass'
            notes = 'New Item added'
          } else {
            status = 'Pass'
            notes = 'New Item added'
          }
          logRows.push({ row_no: rowIdx, document_number: docNum, status, notes })
        })
        fetch(apiUrl('/api/documents/import-log'), {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify({ rows: logRows })
        })
          .then(res => res.blob())
          .then(blob => {
            const url = window.URL.createObjectURL(blob)
            const a = document.createElement('a')
            a.href = url
            a.download = 'import_log.xlsx'
            a.click()
            window.URL.revokeObjectURL(url)
          })
          .catch(() => alert('Failed to generate import log'))
      } catch {
        alert('Failed to generate import log')
      }
    }
    reader.readAsArrayBuffer(file)
  }

  const saveFile = (file, filename) => {
    const url = window.URL.createObjectURL(file)
    const link = document.createElement('a')
    link.href = url
    link.download = filename
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
    window.URL.revokeObjectURL(url)
  }

  const sendImport = (file, confirm, overwrite = false) => {
    const fd = new FormData()
    fd.append('file', file)
    return fetch(apiUrl(`/api/documents/import?confirm=${confirm}&overwrite=${overwrite}&project_id=${projectId}`), {
      method: 'POST',
      headers: { 'X-User-ID': String(userId) },
      credentials: 'include',
      body: fd
    }).then(res => res.json())
  }

  const handleImportConfirm = (file, overwrite, preview: any = {}) => {
    setImporting(true)
    setImportProgress(0)
    sendImport(file, true, overwrite)
      .then(result => {
        setImportProgress(100)
        const addedCount = result.created ?? 0
        const overwrittenCount = result.updated ?? 0
        const skippedCount = result.skipped ?? 0
        const failedCount = result.errors ?? 0
        setReportInfo({ file, overwrite, preview, added: addedCount, overwritten: overwrittenCount, skipped: skippedCount, failed: failedCount, total: result.total_processed ?? preview.total ?? 0, failedRows: result.failed ?? [], skippedRows: result.skipped_rows ?? [], rowStatuses: result.row_statuses ?? {} })
        fetchDocuments()
      })
      .catch(err => {
        setImportPreviewData({ error: err?.message || 'Import failed (server error)', file, total: 0, importable: 0, invalid_dates: [], invalid_locations: [], invalid_categories: [], duplicates: [], existing: [], existing_revisions: [] });
        setImportPreviewOpen(true);
      })
      .finally(() => setImporting(false))
  }

  const processImportFile = file => {
    sendImport(file, false)
      .then(summary => {
        if (summary.error) {
          setImportPreviewData({ error: summary.error, file, total: 0, importable: 0, invalid_dates: [], invalid_locations: [], invalid_categories: [], duplicates: [], existing: [], existing_revisions: [] });
          setImportPreviewOpen(true);
          return;
        }
        setImportPreviewData({ ...summary, file });
        setImportPreviewOpen(true);
      })
      .catch(err => {
        setImportPreviewData({ error: err?.message || 'Import failed (server error)', file, total: 0, importable: 0, invalid_dates: [], invalid_locations: [], invalid_categories: [], duplicates: [], existing: [], existing_revisions: [] });
        setImportPreviewOpen(true);
      })
  }

  const handlePreviewConfirm = (overwrite: boolean) => {
    setImportPreviewOpen(false);
    handleImportConfirm(importPreviewData.file, overwrite, importPreviewData);
  };

  const downloadPreviewLog = () => {
    if (!importPreviewData) return;
    downloadImportReport(importPreviewData.file, [], [], false, importPreviewData);
  };

  const handleFileChange = e => {
    if (readOnly) return
    const file = e.target.files[0]
    e.target.value = ''
    if (!file) return
    processImportFile(file)
  }

  const handleImportClick = () => {
    if (readOnly) return
    setImportSourceOpen(true)
  }

  const handleImportSource = isThird => {
    setImportSourceOpen(false)
    if (isThird) {
      setMappingOpen(true)
    } else {
      fileInputRef.current && fileInputRef.current.click()
    }
  }

  const handleMappingChange = field => (_, value) => {
    setMapping(prev => ({ ...prev, [field]: value || '' }))
  }

  const rowsToWorkbook = rows => {
    const data = [
      ['Number','Name','Location','Category','Revision','Status','Issue Date','Resubmission Reason'],
      ...rows.map(r => [
        r.document_number || '',
        r.document_name || '',
        r.document_location || '',
        r.document_category || '',
        r.document_revision || '',
        r.document_status || '',
        r.issue_date || '',
        r.resubmission_reason || ''
      ])
    ]
    const ws = XLSX.utils.aoa_to_sheet(data)
    const wb = XLSX.utils.book_new()
    XLSX.utils.book_append_sheet(wb, ws, 'Sheet1')
    const wbout = XLSX.write(wb, { bookType: 'xlsx', type: 'array' })
    return new File([wbout], 'mapped.xlsx', {
      type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
    })
  }

  const handleMappingSubmit = () => {
    if (!mapFile) { alert('Select a file'); return }
    const fd = new FormData()
    fd.append('file', mapFile)
    fd.append('mapping', JSON.stringify(mapping))
    if (dateFormat) fd.append('date_format', dateFormat)
    fetch(apiUrl('/api/documents/export-map'), { method: 'POST', body: fd })
      .then(res => res.json())
      .then(rows => {
        const file = rowsToWorkbook(rows)
        setMappingOpen(false)
        setMapFile(null)
        processImportFile(file)
      })
      .catch(() => alert('Failed to map file'))
  }

  const handleExport = () => {
    setExporting(true)
    setExportProgress(0)
    setTimeout(() => {
      const rows = []
      documents.forEach(doc => {
        const revisions = doc.revisions && doc.revisions.length ? doc.revisions : [
          {
            revision_code: doc.document_revision,
            status: doc.document_status,
            issue_date: doc.issue_date,
            resubmission_reason: doc.resubmission_reason
          }
        ]
        revisions.forEach(r => {
          const row = {
            document_number: doc.document_number,
            document_name: doc.document_name,
            document_location: doc.document_location,
            document_category: doc.document_category,
            document_revision: r.revision_code,
            document_status: r.status,
            issue_date: r.issue_date,
            resubmission_reason: r.resubmission_reason,
            assigned_changes: doc.assigned_changes
          }
          if (passes(row, undefined) && (!warningsOnly || hasAnyWarning(row))) {
            rows.push(row)
          }
        })
      })
      const file = rowsToWorkbook(rows)
      setExportProgress(100)
      setExporting(false)
      setExportFile(file)
    }, 100)
  }

  // form state for new document
  const [newDocument, setNewDocument] = useState({
    document_number: '',
    document_name: '',
    document_location: '',
    document_category: '',
    document_revision: '',
    document_status: '',
    issue_date: '',
    resubmission_reason: ''
  })

  const [existingDoc, setExistingDoc] = useState(null)

  useEffect(() => {
    if (!newDocument.document_number) {
      setExistingDoc(null)
      return
    }
    fetch(apiUrl(`/api/documents/check?project_id=${projectId}&number=${encodeURIComponent(
        newDocument.document_number
      )}`))
      .then(res => res.json())
      .then(data => {
        setExistingDoc(data.exists ? data.document : null)
      })
      .catch(() => setExistingDoc(null))
  }, [newDocument.document_number, projectId])


  const fetchDocuments = useCallback(() => {
    setLoading(true)
    return fetch(apiUrl(`/api/documents?project_id=${projectId}`))
      .then(res => res.json())
      .then(data => {
      // ─── ADD THESE LINES ───
      console.log('🔴 RAW API documents:', data)
      // ────────────────────────

      const withParsedDates = data.map(d => {
        // pick top-level or first-revision date
        const rawDate =
          d.issue_date ||
          (Array.isArray(d.revisions) && d.revisions[0]?.issue_date) ||
          null

        // ─── AND THIS LINE ───
        console.log(`doc ${d.document_id} → rawDate:`, rawDate, '→ as Date:', rawDate ? new Date(rawDate) : null)
        // ─────────────────────

        return {
          ...d,
          issue_date: rawDate ? new Date(rawDate) : null,
          revisions: (d.revisions || []).map(r => ({
            ...r,
            issue_date: r.issue_date ? new Date(r.issue_date) : null
          }))
        }
      })

      // ─── AND THIS ONE ───
      console.log('🟢 PARSED documents:', withParsedDates)
      // ────────────────────

        setDocuments(withParsedDates)
      })
      .catch(console.error)
      .finally(() => setLoading(false))
  }, [projectId])



  const fetchDesignChanges = useCallback(() => {
    fetch(apiUrl(`/api/design-changes?project_id=${projectId}`))
      .then(res => res.json())
      .then(data => {
        const filtered = data.filter(dc => dc.change_affecting_scope)
        setDesignOptions(filtered)
      })
      .catch(console.error)
  }, [projectId])

  const fetchLocations = useCallback(() => {
    fetch(apiUrl(`/api/document-locations?project_id=${projectId}`))
      .then(res => res.json())
      .then(setLocationList)
      .catch(console.error)
  }, [projectId])

  const fetchCategories = useCallback(() => {
    fetch(apiUrl(`/api/document-categories?project_id=${projectId}`))
      .then(res => res.json())
      .then(setCategoryList)
      .catch(console.error)
  }, [projectId])

  const fetchStatuses = useCallback(() => {
    fetch(apiUrl(`/api/document-statuses?project_id=${projectId}`))
      .then(res => res.json())
      .then(setStatusList)
      .catch(console.error)
  }, [projectId])

  // fetch data on mount
  useEffect(() => {
    if (access === 'none') return
    if (!projectId) {
      setDocuments([])
      setDesignOptions([])
      setLocationList([])
      setCategoryList([])
      setLoading(false)
      return
    }
    fetchDocuments()
    fetchDesignChanges()
    fetchLocations()
    fetchCategories()
    fetchStatuses()
  }, [access, projectId, fetchDocuments, fetchDesignChanges, fetchLocations, fetchCategories, fetchStatuses])

  // helper to check if a row passes current filters
  const passes = (row, exclude) => {
    if (exclude !== 'document_number' && numFilter.length && !numFilter.includes(row.document_number)) return false
    if (exclude !== 'document_name' && nameFilter.length && !nameFilter.includes(row.document_name)) return false
    if (exclude !== 'document_location' && locFilter.length && !locFilter.includes(row.document_location)) return false
    if (exclude !== 'document_category' && catFilter.length && !catFilter.includes(row.document_category)) return false
    if (exclude !== 'document_revision' && revFilter.length && !revFilter.includes(row.document_revision)) return false
    if (exclude !== 'document_status' && statusFilter.length && !statusFilter.includes(row.document_status)) return false
    if (exclude !== 'issue_date' && issueDateFilter.length && !issueDateFilter.includes(row.issue_date)) return false
    if (exclude !== 'resubmission_reason' && reasonFilter.length && !reasonFilter.includes(row.resubmission_reason)) return false
    if (exclude !== 'assigned_changes' && changeFilter.length) {
      const rowChanges = row.assigned_changes || []
      if (!rowChanges.some(c => changeFilter.includes(c))) return false
    }
    return true
  }

  const unique = (rows, field) => Array.from(new Set(rows.map(r => r[field]).filter(v => v !== undefined && v !== null)))

  const numberOptions = unique(documents.filter(r => passes(r, 'document_number')), 'document_number')
  const nameOptions = unique(documents.filter(r => passes(r, 'document_name')), 'document_name')
  const locationOptions = unique(documents.filter(r => passes(r, 'document_location')), 'document_location')
  const categoryOptions = unique(documents.filter(r => passes(r, 'document_category')), 'document_category')
  const revisionOptions = unique(documents.filter(r => passes(r, 'document_revision')), 'document_revision')
  const statusOptions = unique(documents.filter(r => passes(r, 'document_status')), 'document_status')
  const issueDateOptions = unique(documents.filter(r => passes(r, 'issue_date')), 'issue_date')
  const reasonOptions = unique(documents.filter(r => passes(r, 'resubmission_reason')), 'resubmission_reason')
  const changeOptions = Array.from(new Set(documents.filter(r => passes(r, 'assigned_changes')).flatMap(r => r.assigned_changes || [])))

  const duplicateNumbers = useMemo(() => {
    const counts = {}
    documents.forEach(d => {
      const val = d.document_number
      if (val) counts[val] = (counts[val] || 0) + 1
    })
    return new Set(Object.keys(counts).filter(k => counts[k] > 1))
  }, [documents])

  const duplicateNames = useMemo(() => {
    const counts = {}
    documents.forEach(d => {
      const val = d.document_name
      if (val) counts[val] = (counts[val] || 0) + 1
    })
    return new Set(Object.keys(counts).filter(k => counts[k] > 1))
  }, [documents])

  const isNumberWarning = row => duplicateNumbers.has(row.document_number)
  const isNameWarning = row => duplicateNames.has(row.document_name)
  const isLocationWarning = row => !row.document_location
  const isCategoryWarning = row => !row.document_category
  const hasAnyWarning = row =>
    isNumberWarning(row) ||
    isNameWarning(row) ||
    isLocationWarning(row) ||
    isCategoryWarning(row)

  const warningsCount = documents.filter(r => passes(r, undefined) && hasAnyWarning(r)).length

  const filteredDocuments = documents.filter(
    r => passes(r, undefined) && (!warningsOnly || hasAnyWarning(r))
  )

  const displayRows = useMemo(() => {
    const rows = []
    filteredDocuments.forEach(doc => {
      rows.push({ ...doc, rowId: doc.document_id, isSubRow: false })
      if (
        expandedDocs.includes(doc.document_id) &&
        doc.revisions &&
        doc.revisions.length > 1
      ) {
        doc.revisions.slice(1).forEach(r => {
          rows.push({
            ...doc,
            document_revision: r.revision_code,
            document_status: r.status,
//            issue_date: r.issue_date,
            issue_date: r.issue_date ? new Date(r.issue_date) : null,
            resubmission_reason: r.resubmission_reason,
            document_revision_id: r.id,
            rowId: `${doc.document_id}-${r.id}`,
            isSubRow: true
          })
        })
      }
    })
    return rows
  }, [filteredDocuments, expandedDocs])

  // Add
  const handleAdd = () => {
    if (readOnly) return
    const payload = { ...newDocument, project_id: projectId }
    if (existingDoc) {
      delete payload.document_name
      delete payload.document_location
      delete payload.document_category
    }
    fetch(apiUrl('/api/documents'), {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload)
    })
      .then(res => {
        if (res.status === 409) {
          return res.json().then(data => {
            if (
              existingDoc &&
              window.confirm('Revision already exists. Update existing revision?')
            ) {
              return fetch(apiUrl(`/api/documents/${existingDoc.document_id}`), {
                  method: 'PUT',
                  headers: { 'Content-Type': 'application/json' },
                  body: JSON.stringify({
                    document_revision: newDocument.document_revision,
                    document_status: newDocument.document_status,
                    issue_date: newDocument.issue_date,
                    resubmission_reason: newDocument.resubmission_reason
                  })
                }).then(r => {
                if (!r.ok) throw new Error('Update failed')
                return r.json()
              })
            }
            throw new Error(data.error || 'Add failed')
          })
        }
        if (!res.ok) throw new Error('Add failed')
        return res.json()
      })
      .then(() => {
        setNewDocument({
          document_number: '',
          document_name: '',
          document_location: '',
          document_category: '',
          document_revision: '',
          document_status: '',
          issue_date: '',
          resubmission_reason: ''
        })
        setExistingDoc(null)
        fetchDocuments()
      })
      .catch(err => alert(err.message))
  }

  // Delete
  const handleDelete = (id: string) => () => {
    if (readOnly) return
    if (!window.confirm('Delete this document?')) return
    const docId = String(id).split('-')[0]
    fetch(apiUrl(`/api/documents/${docId}`), {
      method: 'DELETE'
    })
      .then(res => {
        if (!res.ok) throw new Error('Delete failed')
        fetchDocuments()
      })
      .catch(err => alert(err.message))
  }

  const handleDeleteAll = () => {
    if (!canDeleteAll) return
    if (!window.confirm('Delete ALL documents?')) return
    setDeletingAll(true)
    fetch(apiUrl(`/api/documents/delete-all?project_id=${projectId}`), {
      method: 'DELETE',
      headers: { 'X-User-ID': userId }
    })
      .then(res => {
        setDeletingAll(false)
        if (!res.ok) throw new Error('Delete all failed')
        fetchDocuments()
      })
      .catch(err => {
        setDeletingAll(false)
        alert(err.message)
      })
  }

  // Toggle a row into edit mode
  const handleEditClick = (id: string) => () => {
    if (readOnly) return
    setRowModesModel({ ...rowModesModel, [id]: { mode: GridRowModes.Edit } })
  }

  // Save (exit edit mode – will call processRowUpdate)
  const handleSaveClick = (id: string) => () => {
    if (readOnly) return
    setRowModesModel({ ...rowModesModel, [id]: { mode: GridRowModes.View } })
  }

  // Cancel edit
  const handleCancelClick = (id: string) => () => {
    if (readOnly) return
    setRowModesModel({
      ...rowModesModel,
      [id]: { mode: GridRowModes.View, ignoreModifications: true }
    })
  }

  // Called by DataGrid when you save a row
  const processRowUpdate = (updatedRow) => {
    const { document_id, document_number, document_name, document_location, document_category, document_revision, document_status, issue_date, resubmission_reason, assigned_changes, isSubRow, document_revision_id } = updatedRow

    if (isSubRow) {
      return fetch(apiUrl(`/api/document-revisions/${document_revision_id}`), {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          revision_code: document_revision,
          status: document_status,
          issue_date: issue_date ? new Date(issue_date).toISOString().slice(0, 10) : '',
          resubmission_reason
        })
      })
        .then(res => {
          if (!res.ok) throw new Error('Update failed')
          return res.json()
        })
        .then(() => {
          fetchDocuments()
          return updatedRow
        })
        .catch(err => {
          alert(err.message)
          throw err
        })
    }

    return fetch(apiUrl(`/api/documents/${document_id}`), {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        document_number,
        document_name,
        document_location,
        document_category,
        document_revision,
        document_status,
        issue_date: issue_date ? new Date(issue_date).toISOString().slice(0, 10) : '',
        resubmission_reason,
        assigned_changes
      })
    })
      .then(res => {
        if (!res.ok) throw new Error('Update failed')
        return res.json()
      })
      .then(() => {
        fetchDocuments()
        return updatedRow
      })
      .catch(err => {
        alert(err.message)
        throw err
      })
  }


  const handleCellKeyDown = (params, event) => {
    if (event.ctrlKey && event.key === 'c') {
      setCopiedCell({ field: params.field, value: params.value })
    }
    if (event.ctrlKey && event.key === 'v') {
      if (
        copiedCell &&
        copiedCell.field === params.field &&
        params.field !== 'document_number'
      ) {
        event.preventDefault()
        const rowsToUpdate = [params.id]
        Promise.all(
          rowsToUpdate.map(id => {
            const row = displayRows.find(r => r.rowId === id)
            if (!row) return Promise.resolve()
            const updatedRow = { ...row, [params.field]: copiedCell.value }
            return processRowUpdate(updatedRow)
          })
        ).then(() => fetchDocuments())
      }
    }
  }

  const toggleExpand = id => {
    setExpandedDocs(prev =>
      prev.includes(id) ? prev.filter(d => d !== id) : [...prev, id]
    )
  }

  // Column definitions
  const columns: GridColDef[] = [
    {
      field: 'expand',
      headerName: '',
      width: 30,
      sortable: false,
      filterable: false,
      renderCell: params => {
        if (params.row.isSubRow) return null
        const hasMore = (params.row.revisions || []).length > 1
        if (!hasMore) return null
        const isOpen = expandedDocs.includes(params.row.document_id)
        return (
          <span style={{ cursor: 'pointer' }} onClick={() => toggleExpand(params.row.document_id)}>
            {isOpen ? '▼' : '▶'}
          </span>
        )
      }
    },
    {
      field: 'document_number',
      headerName: 'Number',
      flex: 1.5,
      editable: !readOnly,
      cellClassName: params =>
        duplicateNumbers.has(params.value) ? warningColorClass : ''
    },
    {
      field: 'document_name',
      headerName: 'Name',
      flex: 4,
      editable: !readOnly,
      cellClassName: params =>
        duplicateNames.has(params.value) ? warningColorClass : ''

    },
    {
      field: 'document_location',
      headerName: 'Location',
      flex: 1,
      editable: !readOnly,
      cellClassName: params => (!params.value ? warningColorClass : ''),
      type: 'singleSelect',
      valueOptions: locationList.map(l => l.document_location)
    },
    {
      field: 'document_category',
      headerName: 'Category',
      flex: 1.5,
      editable: !readOnly,
      cellClassName: params => (!params.value ? warningColorClass : ''),
      type: 'singleSelect',
      valueOptions: categoryList.map(c => c.document_category)
    },
    {
      field: 'document_revision',
      headerName: 'Revision',
      flex: 1,
      editable: !readOnly
    },
    {
      field: 'document_status',
      headerName: 'Status',
      flex: 1,
      editable: !readOnly,
      type: 'singleSelect',
      valueOptions: statusList.map(s => s.document_status)
    },
    {
      field: 'issue_date',
      headerName: 'Issue Date',
      flex: 1,
      editable: !readOnly,
      renderCell: params => {
        const d = params.row.issue_date
        if (d instanceof Date) {
            return d.toISOString().slice(0, 10)
        }
        return ''
      },
      renderEditCell: params => (
        <TextField
          type="date"
          value={params.value ? params.value.toISOString().slice(0, 10) : ''}
          onChange={e =>
            params.api.setEditCellValue({
              id: params.id,
              field: params.field,
              value: e.target.value ? new Date(e.target.value) : null
            })
          }
          size="small"
          InputLabelProps={{ shrink: true }}
        />
      )
    },
    {
      field: 'resubmission_reason',
      headerName: 'Resubmission Reason',
      flex: 2,
      editable: !readOnly,
      renderEditCell: params => (
        <TextField
          value={params.value || ''}
          onChange={e =>
            params.api.setEditCellValue({ id: params.id, field: params.field, value: e.target.value })
          }
          size="small"
          fullWidth
        />
      )
    },
    {
      field: 'assigned_changes',
      headerName: 'Design Changes',
      flex: 2.2,
      editable: !readOnly,
      
      renderCell: params => (params.value || []).join(', '),
      renderEditCell: params => (
        <Autocomplete
          multiple
          options={designOptions}
          getOptionLabel={o => `${o.change_number} - ${o.change_name}`}
          filterOptions={designFilter}
          value={designOptions.filter(o => (params.value || []).includes(o.change_number))}
          onChange={(_, vals) =>
            params.api.setEditCellValue({ id: params.id, field: params.field, value: vals.map(v => v.change_number) })
          }
          sx={{ width: '100%' }}
          renderInput={p => <TextField {...p} size="small" fullWidth />}
        />
      )
    },
    {
      field: 'actions',
      type: 'actions',
      headerName: 'Actions',
      flex: 1.2,
      getActions: ({ id }) => {
        const row = displayRows.find(r => r.rowId === id)
        if (readOnly) return []
        const isEditing = rowModesModel[id]?.mode === GridRowModes.Edit
        if (isEditing) {
          return [
            <GridActionsCellItem
              icon={<Tooltip title="Save"><SaveIcon /></Tooltip>}
              label="Save"
              onClick={handleSaveClick(String(id))}
              showInMenu={false}
              key="save"
            />,
            <GridActionsCellItem
              icon={<Tooltip title="Cancel"><CancelIcon /></Tooltip>}
              label="Cancel"
              className="textPrimary"
              onClick={handleCancelClick(String(id))}
              showInMenu={false}
              key="cancel"
            />
          ]
        }
        const actions = [
          <GridActionsCellItem
            icon={<Tooltip title="Edit"><EditIcon /></Tooltip>}
            label="Edit"
            onClick={handleEditClick(String(id))}
            showInMenu={false}
            key="edit"
          />
        ]
        if (!row?.isSubRow) {
          actions.push(
            <GridActionsCellItem
              icon={<Tooltip title="Delete"><DeleteIcon /></Tooltip>}
              label="Delete"
              onClick={handleDelete(String(id))}
              showInMenu={false}
              key="delete"
            />
          )
        }
        return actions
      }
    }
  ]


  if (access === 'none') {
    return <div>Access denied</div>
  }

  if (!projectId) {
    return <div>Please select a project.</div>
  }

  return (
    <div style={{ padding: 16 }}>
      {/* Add form */}
      {!readOnly && (
        <Accordion expanded={addOpen} onChange={() => setAddOpen(o => !o)} sx={{ mb: 2 }}>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography>Add Document</Typography>
          </AccordionSummary>
          <AccordionDetails>
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
        <TextField
          label="Number"
          value={newDocument.document_number}
          onChange={e =>
            setNewDocument({ ...newDocument, document_number: e.target.value })
          }
        />
        {!existingDoc && (
          <>
            <TextField
              label="Name"
              value={newDocument.document_name}
              onChange={e =>
                setNewDocument({ ...newDocument, document_name: e.target.value })
              }
            />
            <Autocomplete
              options={locationList.map(l => l.document_location)}
              value={newDocument.document_location}
              onChange={(_, v) =>
                setNewDocument({ ...newDocument, document_location: v || '' })
              }
              renderInput={p => <TextField {...p} label="Location" />}
              sx={{ width: 150 }}
            />
            <Autocomplete
              options={categoryList.map(c => c.document_category)}
              value={newDocument.document_category}
              onChange={(_, v) =>
                setNewDocument({ ...newDocument, document_category: v || '' })
              }
              renderInput={p => <TextField {...p} label="Category" />}
              sx={{ width: 150 }}
            />
          </>
        )}
        <TextField
          label="Revision"
          value={newDocument.document_revision}
          onChange={e =>
            setNewDocument({ ...newDocument, document_revision: e.target.value })
          }
          sx={{ width: 120 }}
        />
        <Autocomplete
          options={statusList.map(s => s.document_status)}
          value={newDocument.document_status}
          onChange={(_, v) =>
            setNewDocument({ ...newDocument, document_status: v || '' })
          }
          renderInput={p => <TextField {...p} label="Status" />}
          sx={{ width: 120 }}
        />
        <TextField
          label="Issue Date"
          type="date"
          value={newDocument.issue_date}
          onChange={e =>
            setNewDocument({ ...newDocument, issue_date: e.target.value })
          }
          InputLabelProps={{ shrink: true }}
          sx={{ width: 160 }}
        />
        <TextField
          label="Resubmission Reason"
          value={newDocument.resubmission_reason}
          onChange={e =>
            setNewDocument({ ...newDocument, resubmission_reason: e.target.value })
          }
          sx={{ width: 200 }}
        />
        <Button variant="contained" onClick={handleAdd}>
          {existingDoc ? 'Add Revision' : 'Add Document'}
        </Button>
            </div>
          </AccordionDetails>
        </Accordion>
      )}

      {/* Filters aligned with headers */}
      <Accordion expanded={filtersOpen} onChange={() => setFiltersOpen(o => !o)} sx={{ mb: 2 }}>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Typography>Filters</Typography>
        </AccordionSummary>
        <AccordionDetails>
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: '1.5fr 4fr 1fr 1.5fr 1fr 1fr 1fr 2fr 2.2fr 1.2fr',
          gap: 8,
          marginBottom: 8
        }}
      >
        <Autocomplete
          multiple
          size="small"
          options={numberOptions}
          value={numFilter}
          disableCloseOnSelect
          onChange={(_, v) => setNumFilter(v)}
          PopperComponent={CustomPopper}
          renderOption={(props, option, { selected }) => (
            <li {...props} style={{ fontSize: '0.8rem' }}>
              <Checkbox checked={selected} sx={{ mr: 1 }} />
              {option}
            </li>
          )}
          renderInput={p => <TextField {...p} label="Number" />}
        />
        <Autocomplete
          multiple
          size="small"
          options={nameOptions}
          value={nameFilter}
          disableCloseOnSelect
          onChange={(_, v) => setNameFilter(v)}
          PopperComponent={CustomPopper}
          renderOption={(props, option, { selected }) => (
            <li {...props} style={{ fontSize: '0.8rem' }}>
              <Checkbox checked={selected} sx={{ mr: 1 }} />
              {option}
            </li>
          )}
          renderInput={p => <TextField {...p} label="Name" />}
        />
        <Autocomplete
          multiple
          size="small"
          options={locationOptions}
          value={locFilter}
          disableCloseOnSelect
          onChange={(_, v) => setLocFilter(v)}
          PopperComponent={CustomPopper}
          renderOption={(props, option, { selected }) => (
            <li {...props} style={{ fontSize: '0.8rem' }}>
              <Checkbox checked={selected} sx={{ mr: 1 }} />
              {option}
            </li>
          )}
          renderInput={p => <TextField {...p} label="Location" />}
        />
        <Autocomplete
          multiple
          size="small"
          options={categoryOptions}
          value={catFilter}
          disableCloseOnSelect
          onChange={(_, v) => setCatFilter(v)}
          PopperComponent={CustomPopper}
          renderOption={(props, option, { selected }) => (
            <li {...props} style={{ fontSize: '0.8rem' }}>
              <Checkbox checked={selected} sx={{ mr: 1 }} />
              {option}
            </li>
          )}
          renderInput={p => <TextField {...p} label="Category" />}
        />
        <Autocomplete
          multiple
          size="small"
          options={revisionOptions}
          value={revFilter}
          disableCloseOnSelect
          onChange={(_, v) => setRevFilter(v)}
          PopperComponent={CustomPopper}
          renderOption={(props, option, { selected }) => (
            <li {...props} style={{ fontSize: '0.8rem' }}>
              <Checkbox checked={selected} sx={{ mr: 1 }} />
              {option}
            </li>
          )}
          renderInput={p => <TextField {...p} label="Revision" />}
        />
        <Autocomplete
          multiple
          size="small"
          options={statusOptions}
          value={statusFilter}
          disableCloseOnSelect
          onChange={(_, v) => setStatusFilter(v)}
          PopperComponent={CustomPopper}
          renderOption={(props, option, { selected }) => (
            <li {...props} style={{ fontSize: '0.8rem' }}>
              <Checkbox checked={selected} sx={{ mr: 1 }} />
              {option}
            </li>
          )}
          renderInput={p => <TextField {...p} label="Status" />}
        />
        <Autocomplete
          multiple
          size="small"
          options={issueDateOptions}
          value={issueDateFilter}
          disableCloseOnSelect
          onChange={(_, v) => setIssueDateFilter(v)}
          PopperComponent={CustomPopper}
          renderOption={(props, option, { selected }) => (
            <li {...props} style={{ fontSize: '0.8rem' }}>
              <Checkbox checked={selected} sx={{ mr: 1 }} />
              {option}
            </li>
          )}
          renderInput={p => <TextField {...p} label="Issue Date" />}
        />
        <Autocomplete
          multiple
          size="small"
          options={reasonOptions}
          value={reasonFilter}
          disableCloseOnSelect
          onChange={(_, v) => setReasonFilter(v)}
          PopperComponent={CustomPopper}
          renderOption={(props, option, { selected }) => (
            <li {...props} style={{ fontSize: '0.8rem' }}>
              <Checkbox checked={selected} sx={{ mr: 1 }} />
              {option}
            </li>
          )}
          renderInput={p => <TextField {...p} label="Resubmission Reason" />}
        />
        <Autocomplete
          multiple
          size="small"
          options={changeOptions}
          value={changeFilter}
          disableCloseOnSelect
          onChange={(_, v) => setChangeFilter(v)}
          PopperComponent={CustomPopper}
          renderOption={(props, option, { selected }) => (
            <li {...props} style={{ fontSize: '0.8rem' }}>
              <Checkbox checked={selected} sx={{ mr: 1 }} />
              {option}
            </li>
          )}
          renderInput={p => <TextField {...p} label="Design Change" />}
        />
        <div style={{ display: 'flex', gap: 4 }}>
          <Button
            size="small"
            startIcon={<WarningIcon />}
            variant={warningsOnly ? 'contained' : 'outlined'}
            onClick={() => setWarningsOnly(w => !w)}
          >
            {warningsCount}
          </Button>
          <Button
            size="small"
            onClick={() => {
              setNumFilter([])
              setNameFilter([])
              setLocFilter([])
              setCatFilter([])
              setRevFilter([])
              setStatusFilter([])
              setIssueDateFilter([])
              setReasonFilter([])
              setChangeFilter([])
              setWarningsOnly(false)
            }}
          >
            <FilterAltOffIcon />
          </Button>
        </div>
      </div>
        </AccordionDetails>
      </Accordion>

      {/* DataGrid */}
      {/* Limit width so no filler column is created */}
      <div style={{ height: 550, width: '100%' }}>
        <DataGrid
          rows={displayRows}
          columns={columns}
          loading={loading}
          getRowId={row => row.rowId}
          editMode={readOnly ? undefined : 'row'}
          rowModesModel={rowModesModel}
          onRowModesModelChange={setRowModesModel}
          processRowUpdate={readOnly ? undefined : processRowUpdate}
          onCellKeyDown={handleCellKeyDown}
          getRowClassName={params => getSubRowClass(params.row)}
          paginationModel={{ page, pageSize }}
          onPaginationModelChange={(model) => { setPage(model.page); setPageSize(model.pageSize) }}
          pageSizeOptions={[10, 25, 50]}
        />
      </div>

      <div style={{ marginTop: 16 }}>
        <Button variant="outlined" onClick={handleDownloadTemplate} style={{ marginRight: 8 }}>
          Export Template
        </Button>
        <Button variant="contained" onClick={handleExport} style={{ marginRight: 8 }}>
          Export To Excel
        </Button>
        <Button variant="contained" onClick={handleImportClick} disabled={readOnly} style={{ marginRight: 8 }}>
          Import From Excel
        </Button>
        {canDeleteAll && (
          <Button
            variant="contained"
            color="error"
            onClick={handleDeleteAll}
            style={{ marginLeft: 8 }}
          >
            Delete All
          </Button>
        )}
        <input
          type="file"
          accept=".xlsx"
          ref={fileInputRef}
          onChange={handleFileChange}
          style={{ display: 'none' }}
        />
        <Dialog open={importSourceOpen} onClose={() => setImportSourceOpen(false)}>
          <DialogTitle>Import Source</DialogTitle>
          <DialogContent>
            <Typography>Is this Excel file from a third-party source?</Typography>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => handleImportSource(false)}>Template</Button>
            <Button variant="contained" onClick={() => handleImportSource(true)}>Third Party</Button>
          </DialogActions>
        </Dialog>
        <Dialog open={mappingOpen} onClose={() => setMappingOpen(false)} maxWidth="sm" fullWidth>
          <DialogTitle>Column Mapping</DialogTitle>
          <DialogContent>
            <input type="file" accept=".xlsx" onChange={e => setMapFile(e.target.files[0])} style={{ marginBottom: 16 }} />
            {mappingFields.map(field => (
              <TextField
                key={field}
                select
                label={field.replace(/_/g, ' ')}
                value={mapping[field]}
                onChange={e => handleMappingChange(field)(null, e.target.value)}
                fullWidth
                margin="dense"
              >
                {fileHeaders.map(h => (
                  <MenuItem key={h} value={h}>
                    {h}
                  </MenuItem>
                ))}
              </TextField>
            ))}
            <TextField
              select
              label="Date Format"
              value={dateFormat}
              onChange={e => setDateFormat(e.target.value)}
              fullWidth
              margin="dense"
            >
              {['dd.mm.yyyy','mm/dd/yyyy','yyyy-mm-dd','dd/mm/yyyy'].map(fmt => (
                <MenuItem key={fmt} value={fmt}>{fmt}</MenuItem>
              ))}
            </TextField>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setMappingOpen(false)}>Cancel</Button>
            <Button variant="contained" onClick={handleMappingSubmit}>Import</Button>
          </DialogActions>
        </Dialog>
        <Dialog open={importing}>
          <DialogTitle>Importing...</DialogTitle>
          <DialogContent sx={{ minWidth: 300 }}>
            <LinearProgress variant="determinate" value={importProgress} />
            <Typography sx={{ mt: 1 }} align="center">{importProgress}%</Typography>
          </DialogContent>
        </Dialog>
        <Dialog open={deletingAll}>
          <DialogTitle>Deleting...</DialogTitle>
          <DialogContent sx={{ minWidth: 300 }}>
            <LinearProgress />
          </DialogContent>
        </Dialog>
        {/* Import Preview Dialog */}
        <Dialog open={importPreviewOpen} onClose={() => setImportPreviewOpen(false)} maxWidth="xs" fullWidth>
          <DialogTitle>{importPreviewData?.error ? 'Import Failed' : 'Import Preview'}</DialogTitle>
          <DialogContent>
            {importPreviewData && (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 12, marginTop: 8 }}>
                {importPreviewData.error && (
                  <div style={{ background: '#fff3f3', border: '1px solid #f44336', borderRadius: 4, padding: '8px 12px' }}>
                    <Typography color="error.main" variant="body2">{importPreviewData.error}</Typography>
                  </div>
                )}
                {!importPreviewData.error && (<>
                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                  <Typography>Total rows in file:</Typography>
                  <Typography fontWeight={600}>{importPreviewData.total}</Typography>
                </div>
                {(importPreviewData.invalid_dates?.length > 0) && (
                  <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                    <Typography color="error.main">Invalid dates:</Typography>
                    <Typography fontWeight={600} color="error.main">{importPreviewData.invalid_dates.length}</Typography>
                  </div>
                )}
                {(importPreviewData.invalid_locations?.length > 0) && (
                  <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                    <Typography color="error.main">Invalid locations:</Typography>
                    <Typography fontWeight={600} color="error.main">{importPreviewData.invalid_locations.length}</Typography>
                  </div>
                )}
                {(importPreviewData.invalid_categories?.length > 0) && (
                  <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                    <Typography color="error.main">Invalid categories:</Typography>
                    <Typography fontWeight={600} color="error.main">{importPreviewData.invalid_categories.length}</Typography>
                  </div>
                )}
                {(importPreviewData.duplicates?.length > 0) && (
                  <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                    <Typography color="error.main">Duplicates in file:</Typography>
                    <Typography fontWeight={600} color="error.main">{importPreviewData.duplicates.length}</Typography>
                  </div>
                )}
                {(importPreviewData.existing?.length > 0 || importPreviewData.existing_revisions?.length > 0) && (
                  <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                    <Typography color="warning.main">Already exist:</Typography>
                    <Typography fontWeight={600} color="warning.main">{importPreviewData.existing?.length || 0}</Typography>
                  </div>
                )}
                <div style={{ display: 'flex', justifyContent: 'space-between', borderTop: '1px solid #e0e0e0', paddingTop: 8 }}>
                  <Typography fontWeight={600}>Rows to import:</Typography>
                  <Typography fontWeight={600}>{importPreviewData.importable}</Typography>
                </div>
                </>)}
              </div>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setImportPreviewOpen(false)}>{importPreviewData?.error ? 'Close' : 'Cancel'}</Button>
            {!importPreviewData?.error && ((importPreviewData?.existing?.length > 0 || importPreviewData?.existing_revisions?.length > 0) ? (
              <>
                <Button variant="outlined" onClick={() => handlePreviewConfirm(false)}>Import (Skip Existing)</Button>
                <Button variant="outlined" onClick={() => handlePreviewConfirm(true)}>Import (Overwrite)</Button>
              </>
            ) : (
              <Button variant="contained" onClick={() => handlePreviewConfirm(false)} disabled={!importPreviewData || importPreviewData.importable === 0}>
                Proceed with Import
              </Button>
            ))}
          </DialogActions>
        </Dialog>
        {reportInfo && !importing && (
          <Dialog open={true} onClose={() => setReportInfo(null)} maxWidth="xs" fullWidth>
            <DialogTitle>Import Complete</DialogTitle>
            <DialogContent>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 12, marginTop: 8 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                  <Typography>Total rows in file:</Typography>
                  <Typography fontWeight={600}>{reportInfo.total}</Typography>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                  <Typography color="success.main">New items added:</Typography>
                  <Typography fontWeight={600} color="success.main">{reportInfo.added}</Typography>
                </div>
                {reportInfo.overwritten > 0 && (
                  <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                    <Typography color="warning.main">Items overwritten:</Typography>
                    <Typography fontWeight={600} color="warning.main">{reportInfo.overwritten}</Typography>
                  </div>
                )}
                {reportInfo.skipped > 0 && (
                  <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                    <Typography color="text.secondary">Existing kept (skipped):</Typography>
                    <Typography fontWeight={600} color="text.secondary">{reportInfo.skipped}</Typography>
                  </div>
                )}
                {reportInfo.failed > 0 && (
                  <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                    <Typography color="error.main">Failed rows:</Typography>
                    <Typography fontWeight={600} color="error.main">{reportInfo.failed}</Typography>
                  </div>
                )}
                <div style={{ display: 'flex', justifyContent: 'space-between', borderTop: '1px solid #e0e0e0', paddingTop: 8 }}>
                  <Typography fontWeight={600}>Total changes:</Typography>
                  <Typography fontWeight={600}>{(reportInfo.added || 0) + (reportInfo.overwritten || 0)}</Typography>
                </div>
              </div>
            </DialogContent>
            <DialogActions>
              <Button onClick={() => { downloadImportReport(reportInfo.file, reportInfo.failedRows, reportInfo.skippedRows, reportInfo.overwrite, reportInfo.preview, reportInfo.rowStatuses); }}>Download Import Log</Button>
              <Button onClick={() => setReportInfo(null)} variant="contained">Close</Button>
            </DialogActions>
          </Dialog>
        )}
        <Dialog open={exporting}>
          <DialogTitle>Exporting...</DialogTitle>
          <DialogContent sx={{ minWidth: 300 }}>
            <LinearProgress variant="determinate" value={exportProgress} />
            <Typography sx={{ mt: 1 }} align="center">{exportProgress}%</Typography>
          </DialogContent>
        </Dialog>
        {exportFile && !exporting && (
          <Dialog open={true} onClose={() => setExportFile(null)}>
            <DialogTitle>Export Completed</DialogTitle>
            <DialogActions>
              <Button onClick={() => { saveFile(exportFile, 'documents.xlsx'); }}>Save File</Button>
              <Button onClick={() => setExportFile(null)} variant="contained">Close</Button>
            </DialogActions>
          </Dialog>
        )}
      </div>

    </div>
  )
}

export default Documents
